import { collection, onSnapshot, type Unsubscribe } from 'firebase/firestore';
import { ProductSchema, type Product } from '@etiquetachef/types';
import { firestore } from './firebase';
import { snapshotData } from './firestoreNormalize';

const TAG = 'admin/firestoreProducts';

/** Estado da aba Produtos (read-only) do detalhe do tenant. */
export type ProductListResult =
  | { kind: 'LOADING' }
  | { kind: 'READY'; products: Product[] }
  | { kind: 'ERROR'; error: unknown };

/**
 * Lista produtos do tenant (sem ordenação server-side — o agrupamento por
 * categoria e a ordenação acontecem no cliente, espelhando `apps/app`). Docs
 * que falham no parse Zod ficam logados e ignorados (defesa em profundidade
 * contra docs antigos durante migrações de schema). Read-only: o admin só
 * observa; toda mutação de catálogo acontece no app cliente / Android.
 *
 * Origem: sem issue — commit 92e8a3839, 2026-06-10 (aba Produtos por categoria).
 *
 * Contrato: listener em `tenants/{tenantId}/products`; erro de snapshot vira `ERROR` (a tela mostra o alerta).
 *
 * Usado por: `features/products/useProducts.ts`.
 *
 * Arquitetura: rule `match /products/{productId}` sob `match /tenants/{tenantId}`.
 *
 * Dados: `tenants/{tid}/products/{productId}` — schema `ProductSchema` (regras
 * de validade embutidas em `validityRules[]`).
 *
 * Falhas conhecidas:
 *  - Produto fora do schema some só com `console.warn`, sem report (#926).
 */
export function subscribeProducts(
  tenantId: string,
  onResult: (r: ProductListResult) => void,
): Unsubscribe {
  console.info(`${TAG}: subscribe tenant=${tenantId}`);
  const ref = collection(firestore, 'tenants', tenantId, 'products');
  return onSnapshot(
    ref,
    (snap) => {
      const products: Product[] = [];
      for (const d of snap.docs) {
        // Normaliza Timestamp→millis antes do parse Zod (campos de tempo
        // gravados via server podem chegar como `Timestamp` no SDK web).
        const parsed = ProductSchema.safeParse({ id: d.id, ...snapshotData(d) });
        if (parsed.success) {
          products.push(parsed.data);
        } else {
          console.warn(`${TAG}: product ${d.id} ignorado`, parsed.error.issues);
        }
      }
      onResult({ kind: 'READY', products });
    },
    (err) => {
      console.error(`${TAG}: snapshot error`, err);
      onResult({ kind: 'ERROR', error: err });
    },
  );
}
